import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted animate-pulse rounded-lg" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="group relative px-3 lg:px-4 py-2 text-sm font-semibold tracking-wide text-foreground transition-colors hover:text-secondary">
                {item.label}
                <span className="absolute inset-x-4 -bottom-1 h-0.5 scale-x-0 bg-secondary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-lg" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-5 sm:p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block py-3 text-base sm:text-lg font-semibold text-foreground transition-colors hover:text-secondary" onClick={onClose}>
          {item.label}
        </Link>)}

      <div className="mt-5 pt-5 border-t border-border">
        <Button className="w-full rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold tracking-wide" asChild>
          <Link to="/DonatePage" onClick={onClose}>
            Donate Now
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent", isScrolled ? "bg-background/90 backdrop-blur-md shadow-lg border-border/50" : "bg-background")}>
      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex h-14 sm:h-16 md:h-20 lg:h-24 xl:h-28 items-center justify-between gap-3">
          <div className="min-w-0 flex-shrink flex items-center">
            <Link to="/HomePage" className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="https://touching-owl.10web.cloud/wp-content/uploads/2026/03/FFF-Logo.png" alt="Farquhar Fisher Foundation Logo" className="object-contain transition-transform duration-300 hover:scale-105 w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28" />
              <span className="font-bold text-foreground tracking-tight text-sm sm:text-lg md:text-2xl lg:text-3xl leading-tight truncate">
                Farquhar Fisher Foundation
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center justify-center flex-1 px-4 lg:px-8 xl:px-12">
            <MenuProvider menu_id="46" className="w-full flex justify-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-shrink-0">
            <Button asChild className="hidden md:inline-flex rounded-xl bg-secondary px-5 lg:px-6 font-bold text-secondary-foreground shadow-md hover:bg-secondary/90 hover:shadow-secondary/20 hover:shadow-xl transition-all duration-300">
              <Link to="/DonatePage">Donate Now</Link>
            </Button>

            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent hover:text-accent-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full max-h-[calc(100vh-4rem)] md:max-h-[calc(100vh-5rem)] xl:max-h-[calc(100vh-5rem)] overflow-y-auto bg-background/98 backdrop-blur-2xl border-b border-border shadow-2xl md:hidden animate-in slide-in-from-top-5 duration-300">
          <MenuProvider menu_id="46">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}